import { Fragment, useEffect, useState } from "react";
import moment from "moment";

import { InvoiceService } from "../../hoa-don/InvoiceService";
import { RegisterInvoiceService } from "../../../service/invoice/RegisterInvoiceService";
import { CurrencyService } from "../../danh-muc/tien-te/CurrencyService";

import { useToolbar } from "../../../shared/hooks/Toolbar";
import { useDataTable } from "../../../shared/hooks/DataTable";
import { useSign } from "../../../shared/component/Sign";

import { DialogCrup } from "../../hoa-don/component/DialogCrup";
import { DialogView } from "../../hoa-don/component/DialogView";
import { DialogSendEmail } from "../../hoa-don/component/DialogSendEmail";

import { DialogStatement } from "./DialogStatement";
import { DialogAttachment } from "./DialogAttachment";
import DialogBangKe from "./DialogBangKe";
import { StatementInvoiceService } from "../StatementInvoiceService";
import { DialogCreateMultipleInvoiceOptions } from "./DialogCreateMultipleInvoiceOptions";

type MultipleInvoiceMode = "substitute" | "adjustment";

const modeConfig = {
  substitute: {
    header: "Hóa đơn thay thế nhiều",
    createdGroupHeader: "Hóa đơn thay thế",
    actionLabel: "Lập hóa đơn thay thế nhiều",
    actionName: "Thay thế",
    title: "thay thế",
    invoiceStatus: 3,
    relatedInvoiceProperty: 3,
    invoiceType: 1,
    documentType: InvoiceService.documentType_Substitute,
    getList: (params) => InvoiceService.getListSubstitutes(params),
  },
  adjustment: {
    header: "Hóa đơn điều chỉnh nhiều",
    createdGroupHeader: "Hóa đơn điều chỉnh",
    actionLabel: "Lập hóa đơn điều chỉnh nhiều",
    actionName: "Điều chỉnh",
    title: "điều chỉnh",
    invoiceStatus: 2,
    relatedInvoiceProperty: 4,
    invoiceType: 2,
    documentType: InvoiceService.documentType_Adjust,
    getList: (params) => InvoiceService.getListAdjustments(params),
  },
};

const formatDate = (value) => (value ? moment(value).format("DD/MM/YYYY") : "");

const getOriginalInvoiceId = (item) =>
  item?.originalInvoiceId || item?.invoiceId || item?.relatedInvoiceId;

const buildSelectedInvoicesFromInvoiceList = (invoiceList) => {
  const invoiceIds = Array.from(
    new Set(
      (invoiceList?.invoiceListItems || [])
        .map(getOriginalInvoiceId)
        .filter(Boolean),
    ),
  );

  return invoiceIds.map((id) => ({ id }));
};

export const MultipleInvoiceList = ({ mode }: { mode: MultipleInvoiceMode }) => {
  const config = modeConfig[mode];
  const dataKey = "invoiceId";

  const [displayCreateOptions, setDisplayCreateOptions] = useState(false);
  const [displayBangKe, setDisplayBangKe] = useState(false);
  const [displayCrup, setDisplayCrup] = useState(false);
  const [displayStatement, setDisplayStatement] = useState(false);
  const [displayUpload, setDisplayUpload] = useState(false);
  const [displayView, setDisplayView] = useState(false);
  const [displaySendEmail, setDisplaySendEmail] = useState(false);

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [invoiceList, setInvoiceList] = useState(null);
  const [registerInvoice, setRegisterInvoice] = useState(null);
  const [registerInvoices, setRegisterInvoices] = useState([]);
  const [currencies, setCurrencies] = useState(null);
  const [paymentTypes, setPaymentTypes] = useState(null);
  const [statementData, setStatementData] = useState(null);

  useEffect(() => {
    RegisterInvoiceService.getUsingList({
      maxResultCount: 1000,
      Sorting: "creationTime",
      SortType: "DESCEND",
    }).then(({ items }) => {
      const nextItems = (items || []).map((item) => ({
        ...item,
        label: `${item.symbolCode} - ${item.invoiceTypeName}`,
      }));
      setRegisterInvoices(nextItems);
      setRegisterInvoice(nextItems[0] || null);
    });

    CurrencyService.getList({ maxResultCount: 1000 }).then(({ items }) => {
      setCurrencies(items);
    });

    InvoiceService.getPaymentTypes().then(({ items }) => {
      setPaymentTypes(
        (items || []).map((item) => ({ value: item.name, label: item.name })),
      );
    });
  }, []);

  const viewInvoice = (id) => {
    setSelectedInvoice({ id });
    setDisplayView(true);
  };

  const print = (invoice) => {
    StatementInvoiceService.getByInvoice(invoice.invoiceId || invoice.id).then(
      (res) => {
        window.open(StatementInvoiceService.getPdfUrl(res.id), "_blank");
      },
    );
  };

  const {
    render: renderDataTable,
    reloadLazyData,
    selectedItems,
  } = useDataTable({
    header: config.header,
    dataKey,
    filterDelay: 500,
    columns: [
      {
        field: "invoiceSerial",
        header: "Ký hiệu",
        width: 100,
        matchMode: "contains",
      },
      {
        field: "invoiceNumber",
        header: "Số hóa đơn",
        width: 110,
        matchMode: "contains",
        dataType: "custom",
        filterType: "number",
        customCell(rowData): any {
          return (
            <span
              className="underline text-primary"
              onClick={() => viewInvoice(rowData.invoiceId || rowData.id)}
            >
              {rowData.invoiceNumber}
            </span>
          );
        },
        style: { justifyContent: "center" },
      },
      {
        field: "invoiceDate",
        header: "Ngày hóa đơn",
        width: 120,
        matchMode: "equals",
        filterType: "range-date",
        filterField: "FromDate@ToDate",
        dataType: "date",
      },
      {
        field: "sendTaxStatus",
        header: "Trạng thái gửi CQT",
        width: 180,
        matchMode: "equals",
        filterType: "dropdown",
        filterOptions: InvoiceService.sendTaxStatuses,
        dataType: "custom",
        customCell(rowData: any): any {
          const status = InvoiceService.sendTaxStatuses.find(
            (item) => item.value === rowData.sendTaxStatus,
          );
          return (
            <span
              className={`${[3, 4, 5].includes(rowData.sendTaxStatus) ? "underline " : ""}send-tax-status send-tax-status-${rowData.sendTaxStatus}`}
            >
              {status?.label}
            </span>
          );
        },
      },
      {
        field: "buyerLegalName",
        header: "Khách hàng",
        minWidth: 200,
        matchMode: "contains",
        dataType: "custom",
        customCell(rowData: any): any {
          return rowData.buyerLegalName || rowData.buyerDisplayName;
        },
      },
      {
        field: "documentNoView",
        header: "Biên bản",
        width: 110,
        sortable: false,
        dataType: "custom",
        customCell(rowData): any {
          const text = rowData.hasDocument
            ? rowData.documentNo
            : "Lập biên bản";
          const className = rowData.hasDocument
            ? "underline send-tax-status send-tax-status-4"
            : "underline text-primary";
          return (
            <span
              className={className}
              onClick={() => setDisplayStatement(true)}
            >
              {text}
            </span>
          );
        },
        style: { justifyContent: "center" },
      },
      {
        field: "rowActions",
        header: "Thao tác",
        width: 100,
        sortable: false,
        dataType: "custom",
        customCell(rowData: any): any {
          return (
            <div className="flex gap-3">
              <i
                className="pi pi-link"
                title="File đính kèm"
                onClick={() => setDisplayUpload(true)}
              ></i>
              {rowData.hasDocument && (
                <i
                  className="pi pi-print"
                  title="In biên bản"
                  onClick={() => print(rowData)}
                ></i>
              )}
              <i
                className="pi pi-envelope"
                title="Gửi email biên bản cho người mua ký"
                onClick={() => {
                  setDisplaySendEmail(true);
                  StatementInvoiceService.getByInvoice(
                    rowData.invoiceId || rowData.id,
                  ).then((res) => {
                    setStatementData(res);
                  });
                }}
              ></i>
            </div>
          );
        },
        style: { justifyContent: "center" },
      },
      {
        field: "relatedInvoiceListNumber",
        header: "Số bảng kê",
        width: 140,
        matchMode: "contains",
        dataType: "custom",
        customCell(rowData): any {
          return rowData.relatedInvoiceListNumber || rowData.invoiceListNumber;
        },
      },
      {
        field: "relatedInvoiceListDate",
        header: "Ngày bảng kê",
        width: 130,
        matchMode: "equals",
        filterType: "range-date",
        filterField: "relatedInvoiceListDate@relatedInvoiceListDate",
        dataType: "custom",
        customCell(rowData): any {
          return formatDate(
            rowData.relatedInvoiceListDate || rowData.invoiceListDate,
          );
        },
      },
      {
        field: "invoiceNote",
        header: "Lý do sai sót",
        minWidth: 180,
        matchMode: "contains",
      },
    ],
    indexColumnWidth: 40,
    getList: (params) => {
      if (!params.sortType) {
        params.sortType = "DESCEND";
      }
      if (!params.sorting) {
        params.sorting = "invoiceDate";
      }
      params.RelatedInvoiceProperty = config.relatedInvoiceProperty;
      return config.getList(params);
    },
    selectionMode: "single",
  });

  const { renderToolbar } = useToolbar({
    dataKey,
    reloadLazyData,
  });

  const { renderSign, sign } = useSign({ onComplete: reloadLazyData });

  return (
    <Fragment>
      {renderToolbar()}
      {renderDataTable()}
      {renderSign()}

      <DialogCreateMultipleInvoiceOptions
        display={displayCreateOptions}
        setDisplay={setDisplayCreateOptions}
        config={config}
        onCreateInvoice={() => {
          setSelectedInvoices([]);
          setInvoiceList(null);
          setDisplayCrup(true);
        }}
        onCreateInvoiceList={() => {
          setSelectedInvoices([]);
          setInvoiceList(null);
          setDisplayBangKe(true);
        }}
      />

      <DialogBangKe
        display={displayBangKe}
        setDisplay={setDisplayBangKe}
        selectedInvoices={selectedInvoices}
        invoiceType={config.invoiceType}
        registerInvoices={registerInvoices}
        onSubmit={(result) => {
          setInvoiceList(result);
          setSelectedInvoices(buildSelectedInvoicesFromInvoiceList(result));
          setDisplayBangKe(false);
          setDisplayCrup(true);
        }}
      />

      <DialogCrup
        display={displayCrup}
        setDisplay={setDisplayCrup}
        reloadLazyData={reloadLazyData}
        registerInvoices={registerInvoices}
        currencies={currencies}
        paymentTypes={paymentTypes}
        registerInvoice={registerInvoice}
        selectedItem={null}
        selectedInvoices={selectedInvoices}
        invoiceList={invoiceList}
        relatedInvoiceProperty={config.relatedInvoiceProperty}
        invoiceStatus={config.invoiceStatus}
        sign={sign}
      />

      <DialogAttachment
        display={displayUpload}
        setDisplay={setDisplayUpload}
        selectedItem={selectedItems}
      />

      <DialogStatement
        display={displayStatement}
        setDisplay={setDisplayStatement}
        selectedItem={selectedItems}
        documentType={config.documentType}
        reloadLazyData={reloadLazyData}
        title={config.title}
      />

      <DialogView
        display={displayView}
        setDisplay={setDisplayView}
        selectedItem={selectedInvoice}
      />

      <DialogSendEmail
        display={displaySendEmail}
        setDisplay={setDisplaySendEmail}
        sendType={8}
        selectedItems={statementData}
      />
    </Fragment>
  );
};
